import React from 'react';
import { connect } from 'react-redux';
import { clearSiteRoutesCache, updateLegacySessionKey } from '../../store/actions';
import { Link } from 'react-router-dom';
import {
  pullCampaignOffices,
  pullCampaignScripts,
  pullCampaignSiteCanvasses,
  updateOrCreateSiteCanvass,
} from '../../store/api/calls';
import { siteCanvassEditCreateForm } from '../../utils/forms/formFields';

import NavBaseTemplate from '../templates/NavBaseTemplate';
import BaseForm from '../theme/forms/BaseForm';
import DataTable from '../theme/tables/DataTable';
import BaseModal from '../theme/modals/BaseModal';
import FeedbackTile from '../theme/modals/FeedbackTile';
import Moment from 'moment';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Grid } from '@mui/material';

class SiteCanvassScreen extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      editModalOpen: false,
      deleteModalOpen: false,
      editingInstance: {},
      section: 'siteroutes',
      filterActiveToday: false,
    };

    this.formInputs = siteCanvassEditCreateForm;
  }

  componentDidMount() {
    this.handleMountOrCampaignUpdate();
  }

  componentDidUpdate(prevProps) {
    if (this.props.focusedCampaign.id !== prevProps.focusedCampaign.id) {
      this.handleMountOrCampaignUpdate();
    }
  }

  handleMountOrCampaignUpdate = () => {
    pullCampaignSiteCanvasses()
      .then(() => pullCampaignOffices())
      .then(() => pullCampaignScripts())
      .then(() => this.populateCreateEditForm());
  };

  toggleActiveTodayFilter = () => {
    const next = !this.state.filterActiveToday;
    this.setState({ filterActiveToday: next });
    const params = next ? { has_current_shift: true } : {};
    pullCampaignSiteCanvasses(params);
  };

  populateCreateEditForm() {
    const offices = [{ value: null, display: '' }];
    this.props.campaignOffices.forEach((d) => {
      offices.push({ value: d.id, display: d.name });
    });

    const scripts = [{ value: null, display: '' }];
    [...this.props.campaignScripts]
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
      .forEach((d) => {
        scripts.push({ value: d.id, display: d.name });
      });

    this.formInputs = [
      {
        type: 'text',
        title: 'Name',
        dataKey: 'name',
        placeholder: 'Use something descriptive',
      },
      {
        type: 'select',
        title: 'Office',
        dataKey: 'office',
        options: offices,
      },
      {
        type: 'select',
        title: 'Script',
        dataKey: 'script',
        options: scripts,
      },
    ];
  }

  /* -----------------------------
   * Table Data
   * ----------------------------- */

  _filterKeysForDataTable() {
    return this.props.siteCanvasses.map((d) => {
      const office = this.props.campaignOffices.find((o) => o.id === d.office) || {
        name: '',
      };

      const script = this.props.campaignScripts.find((s) => s.id === d.script) || {
        name: '',
      };

      return {
        name: (
          <Link to={`/sitecanvass/${d.id}`} onClick={this.props.clearSiteRoutesCache}>
            {d.name}
          </Link>
        ),
        _name: d.name,

        office: office.name,
        script: script.name,

        created_on: new Moment(d.created_at).format('MM/DD/YY'),
        _created_on: new Moment(d.created_at),

        // RIGHT-MOST ACTION COLUMN — NO HEADER
        '\u200B': (
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            <button
              className="et-datatable-button button button-outline"
              onClick={() => this.onOpenEditModal(d)}
            >
              Edit
            </button>
            <button
              className="et-datatable-button et-datatable-button-red button button-outline"
              onClick={() => this.onOpenDeleteModal(d)}
            >
              Remove
            </button>
          </div>
        ),

        _data: d,
      };
    });
  }

  /* -----------------------------
   * Edit / Delete
   * ----------------------------- */

  onOpenEditModal = (modelInstance = {}) => {
    this.buildInputsWithPrepopulatedFields(modelInstance);
    this.setState({ editModalOpen: true, editingInstance: modelInstance });
  };

  onCloseEditModal = () => {
    this.setState({ editModalOpen: false, editingInstance: {} });
  };

  onOpenDeleteModal = (modelInstance = {}) => {
    this.setState({ deleteModalOpen: true, editingInstance: modelInstance });
  };

  onCloseDeleteModal = () => {
    this.setState({ deleteModalOpen: false, editingInstance: {} });
  };

  buildInputsWithPrepopulatedFields = (modelInstance) => {
    this.formInputs.forEach((d) => {
      d.value = modelInstance[d.dataKey];
    });
  };

  handleUpdateCreateSubmission = (data) => {
    const id = this.state.editingInstance.id;
    const campaign = this.props.focusedCampaign.id;

    if (id) data = { ...data, id };
    data = { ...data, campaign };

    updateOrCreateSiteCanvass(data).then(this.onCloseEditModal);
  };

  handleRemoveSubmission = () => {
    const id = this.state.editingInstance.id;
    updateOrCreateSiteCanvass({ id, is_active: false }).then(this.onCloseDeleteModal);
  };

  /* -----------------------------
   * Render
   * ----------------------------- */

  render() {
    return (
      <NavBaseTemplate>
        <Grid className="button-table-top-wrapper" justifyContent="space-between" alignItems="flex-end">
          <Grid item>
          <a className="button" onClick={() => this.onOpenEditModal({})}>
            + Create Site Canvass
          </a>
          </Grid>
          <Grid item>
          <FormControlLabel
            label="Scheduled Today"
            componentsProps={{ typography: { style: { fontSize: '12px' } } }}
            control={
              <Checkbox
                size="small"
                checked={this.state.filterActiveToday}
                onChange={this.toggleActiveTodayFilter}
              />
            }
          />
          </Grid>
        </Grid>

        <DataTable data={this._filterKeysForDataTable() || []} title="Site Canvasses" />

        <FeedbackTile
          text={
            <div>
              <i className="fas fa-asterisk fa-spin" style={{ marginRight: '15px' }} />
              Loading...
            </div>
          }
          visible={this.props.loading}
        />

        {/* EDIT MODAL */}
        <BaseModal open={this.state.editModalOpen} onClose={this.onCloseEditModal} center>
          <BaseForm
            inputs={this.formInputs}
            onCancel={this.onCloseEditModal}
            onSubmit={this.handleUpdateCreateSubmission}
          />
        </BaseModal>

        {/* DELETE MODAL */}
        <BaseModal open={this.state.deleteModalOpen} onClose={this.onCloseDeleteModal} center>
          <div className="modal-internal-wrapper">
            <p>
              Are you sure you want to remove the site canvass{' '}
              <b>{this.state.editingInstance.name}</b>?
            </p>
            <div className="button-wrapper">
              <div className="button" onClick={this.handleRemoveSubmission}>
                Remove
              </div>
              <div className="button button-outline" onClick={this.onCloseDeleteModal}>
                Cancel
              </div>
            </div>
          </div>
        </BaseModal>
      </NavBaseTemplate>
    );
  }
}

/* -----------------------------
 * Redux
 * ----------------------------- */

const mapStateToProps = (state) => ({
  focusedCampaign: state.session.focusedCampaign,
  siteCanvasses: state.siteCanvasses.data,
  loading: state.siteCanvasses.loading,
  error: state.siteCanvasses.error,
  campaignOffices: state.offices.data,
  campaignScripts: state.scripts.data,
  is_admin: state.permissions.is_admin,
});

const mapDispatchToProps = (dispatch) => ({
  updateLegacySessionKey: (key) => dispatch(updateLegacySessionKey(key)),
  clearSiteRoutesCache: () => dispatch(clearSiteRoutesCache()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SiteCanvassScreen);
